import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Papa from "papaparse";

const TransactionData = () => {
  // Function to get first day of current month
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  };

  // Function to get current date
  const getCurrentDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ 
    currentPage: 1, 
    totalPages: 1, 
    totalItems: 0, 
    limit: 20,
    lastEvaluatedKey: null
  });
  const [filters, setFilters] = useState({
    startDate: getFirstDayOfMonth(), // Changed to start of current month
    endDate: getCurrentDate(), // Changed to current date
    query: "",
    page: 1,
    limit: 20,
    lastEvaluatedKey: null
  });

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const queryParams = new URLSearchParams({
          startDate: filters.startDate,
          endDate: filters.endDate,
          query: filters.query,
          page: filters.page.toString(),
          limit: filters.limit.toString(),
          ...(filters.lastEvaluatedKey ? { lastEvaluatedKey: filters.lastEvaluatedKey } : {})
        });

        const response = await fetch(
          `https://pukfh1sc9a.execute-api.ap-south-1.amazonaws.com/prod/api/demotransaction/transactions?${queryParams}`
        );
        const data = await response.json();
        
        setTransactions(data.transactions || []);
        setPagination({
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          limit: data.pagination.limit,
          lastEvaluatedKey: data.pagination.lastEvaluatedKey
        });
      } catch (error) {
        console.error("Failed to fetch transactions:", error);
      }
    };
    
    fetchTransactions();
  }, [filters]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ 
      ...prev, 
      [name]: value, 
      page: 1, // Reset to first page when filters change
      lastEvaluatedKey: null // Reset last evaluated key
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters((prev) => ({ 
        ...prev, 
        page: newPage,
        // Use the last evaluated key for the next page
        lastEvaluatedKey: pagination.lastEvaluatedKey
      }));
    }
  };

  const handleExport = () => {
    const csv = Papa.unparse(transactions);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "transactions.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex flex-col md:flex-row gap-4">
          <Input 
            type="date" 
            name="startDate" 
            value={filters.startDate} 
            onChange={(e) => handleFilterChange("startDate", e.target.value)} 
          />
          <Input 
            type="date" 
            name="endDate" 
            value={filters.endDate} 
            onChange={(e) => handleFilterChange("endDate", e.target.value)} 
          />
          <Input 
            placeholder="Search name, email, phone, course, or coupon..." 
            name="query" 
            value={filters.query} 
            onChange={(e) => handleFilterChange("query", e.target.value)} 
          />
        </div>
        <Button onClick={handleExport}>Export to CSV</Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Purchase Date</TableHead>
            <TableHead>Purchase Price</TableHead>
            <TableHead>Coupon Code</TableHead>
            <TableHead>Phone No</TableHead>
            <TableHead>Course Name</TableHead>
            <TableHead>Course Category</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.map((transaction, index) => (
            <TableRow key={index}>
              <TableCell>{transaction.UserName}</TableCell>
              <TableCell>{transaction.UserMail}</TableCell>
              <TableCell>{transaction.PurchaseTime}</TableCell>
              <TableCell>{transaction.PurchasePrice}</TableCell>
              <TableCell>{transaction.CouponCode}</TableCell>
              <TableCell>{transaction.UserMobile}</TableCell>
              <TableCell>{transaction.CourseName}</TableCell>
              <TableCell>{transaction.CourseCategory}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex justify-between items-center mt-4">
        <Button
          disabled={pagination.currentPage === 1}
          onClick={() => handlePageChange(pagination.currentPage - 1)}
        >
          Previous
        </Button>
        <div className="flex items-center gap-2">
          <span>
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <span>
            ({pagination.totalItems} total items)
          </span>
        </div>
        <Button
          disabled={pagination.currentPage >= pagination.totalPages}
          onClick={() => handlePageChange(pagination.currentPage + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default TransactionData;